/**
 * Saved views for the sales-metrics page.
 *
 * A "view" is just the page's scope - date range, bucket grain and active tab - so it
 * is small enough to live in localStorage per user. That is deliberately unlike the
 * heatmap's presets (services/heatmap-presets.ts), which persist a nested FilterGroup
 * to the `heatmap_saved_filters` table: reusing that table would mean storing a shape
 * its column was not designed for, and a metrics view has no server-side consumer.
 *
 * If these ever need to be shared between devices or users, this is the seam to move
 * behind Supabase - the function signatures already take a userId.
 */

import { DATE_PRESETS, type MetricsFilter } from "../components/sales-metrics/scope";

const PRESETS_PREFIX = "metrics_saved_views_";
const DEFAULT_PREFIX = "metrics_default_view_";

export interface MetricsSavedView {
  id: string;
  name: string;
  filter: MetricsFilter;
  /** Tab index the view was saved from, so a view restores the whole screen. */
  tab: number;
  /** Presets resolve relative to today; absolute ones pin the exact dates. */
  relativePresetId: string | null;
  isDefault: boolean;
  createdAt: string;
}

function readViews(userId: string): MetricsSavedView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(`${PRESETS_PREFIX}${userId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("Could not read saved metrics views:", err);
    return [];
  }
}

function writeViews(userId: string, views: MetricsSavedView[]): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(`${PRESETS_PREFIX}${userId}`, JSON.stringify(views));
  } catch (err) {
    console.warn("Could not save metrics views:", err);
  }
}

export function getSavedViews(userId: string): MetricsSavedView[] {
  return readViews(userId);
}

export function saveView(
  userId: string,
  name: string,
  filter: MetricsFilter,
  tab: number,
  isDefault: boolean
): MetricsSavedView {
  // If the current range matches a relative preset, remember the PRESET rather than
  // the dates it resolved to - otherwise "Letzte 7 Tage" freezes to one specific week.
  const matching = DATE_PRESETS.find((p) => {
    const r = p.resolve();
    return r.start === filter.start && r.end === filter.end;
  });

  const view: MetricsSavedView = {
    id: `view_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name,
    filter,
    tab,
    relativePresetId: matching?.id ?? null,
    isDefault,
    createdAt: new Date().toISOString(),
  };

  const next = readViews(userId).map((v) =>
    isDefault ? { ...v, isDefault: false } : v
  );
  next.unshift(view);
  writeViews(userId, next);
  if (isDefault) setDefaultView(userId, view.id);
  return view;
}

export function deleteView(userId: string, viewId: string): void {
  writeViews(
    userId,
    readViews(userId).filter((v) => v.id !== viewId)
  );
  if (getDefaultViewId(userId) === viewId) {
    try {
      localStorage.removeItem(`${DEFAULT_PREFIX}${userId}`);
    } catch {
      /* ignore */
    }
  }
}

export function setDefaultView(userId: string, viewId: string): void {
  writeViews(
    userId,
    readViews(userId).map((v) => ({ ...v, isDefault: v.id === viewId }))
  );
  try {
    localStorage.setItem(`${DEFAULT_PREFIX}${userId}`, viewId);
  } catch {
    /* ignore */
  }
}

export function getDefaultViewId(userId: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(`${DEFAULT_PREFIX}${userId}`);
  } catch {
    return null;
  }
}

/**
 * Resolves a saved view back to a live filter. A view saved from a relative preset
 * re-resolves against today, so "Letzte 7 Tage" stays the last 7 days.
 */
export function resolveView(view: MetricsSavedView): MetricsFilter {
  if (view.relativePresetId) {
    const preset = DATE_PRESETS.find((p) => p.id === view.relativePresetId);
    if (preset) return { ...preset.resolve(), grain: view.filter.grain };
  }
  return view.filter;
}
